/**
 * `/help/structure` — Help → Structure.
 *
 * The platform's work-breakdown structure: every section, page and sub-page
 * as one numbered outline, cut from the same data as the left rail. Answers
 * the v1.8 reviewer question "is there an overarching mind-map or WBS where
 * we can see all these connected sections and sub-sections?".
 *
 * Reachable in executive mode, where the Help section is off the rail, from
 * the account menu's "Platform map" entry.
 *
 * @see src/versions/v1/modules/help/WbsTree.transform.ts
 * @see docs/logic/help/structure.md
 */

import { useMemo } from 'react';
import { HelpAnchor, SurfacePageBody, SurfacePageHeader, SurfacePageLayout } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { HelpTabs } from './HelpTabs';
import { WbsTree } from './WbsTree';
import { wbsStats, wbsTree } from './WbsTree.transform';

const STANDFIRST =
  'Every section, page and sub-page as one numbered outline — the same tree the menu on the ' +
  'left is cut from. Codes read section · page · sub-page, so 3.07.2 is Workflows › 07 Parts & ' +
  'Supply › Stock & replenishment. Every row opens its page.';

export function StructureScene() {
  const nodes = useMemo(() => wbsTree(), []);
  const stats = useMemo(() => wbsStats(nodes), [nodes]);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="StructureScene">
        <SurfacePageBody className="@container max-w-[1320px] space-y-6" as="div">
          <SurfacePageHeader
            kicker="Help"
            title="Platform structure"
            description={STANDFIRST}
            actions={<HelpAnchor doc="help/structure" />}
            tabs={<HelpTabs current="structure" />}
          />

          <ul className="flex flex-wrap gap-x-8 gap-y-3">
            {stats.map((stat) => (
              <li key={stat.eyebrow}>
                <span
                  className="block text-[10px] font-semibold uppercase tracking-[0.1em]"
                  style={{ color: 'var(--ink-tertiary)' }}
                >
                  {stat.eyebrow}
                </span>
                <span
                  className="mono block text-[22px] font-semibold leading-tight"
                  style={{ color: 'var(--ink-primary)' }}
                >
                  {stat.value}
                </span>
                <span className="block text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
                  {stat.detail}
                </span>
              </li>
            ))}
          </ul>

          <WbsTree nodes={nodes} />
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
